/**
 * StudentSinavTab.jsx
 * Öğrencinin sınav grubu üyelikleri + önerilen gruplar.
 *
 * Endpointler:
 *   detail.exam_groups — mevcut grup üyelikleri (detail response'dan gelir)
 *   GET  /admin/students/{id}/exam-group-suggestions
 *   POST /admin/exam-groups/{group_id}/members — { user_ids: [userId] }
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Users, GraduationCap, Plus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { EmptyState } from '../../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d, lang) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
}

export default function StudentSinavTab({ detail, userId, onRefresh }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [suggestions, setSuggestions] = useState(null); // null = not loaded

  // Üye olunan gruplar suggestions endpoint'inin member_groups alanından gelir
  // (detail.exam_groups eski fallback olarak korunur)
  const examGroups = suggestions?.member_groups || detail?.exam_groups || [];
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [addingGroupId, setAddingGroupId] = useState(null);

  const fetchSuggestions = useCallback(async () => {
    if (!userId) return;
    setSuggestLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/students/${userId}/exam-group-suggestions`,
        { headers: authHeaders() }
      );
      setSuggestions(r.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setSuggestions({ suggestions: [], genel: [] });
    } finally {
      setSuggestLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, lang]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleAddToGroup = async (groupId) => {
    if (!userId) return;
    setAddingGroupId(groupId);
    try {
      await axios.post(
        `${API}/admin/exam-groups/${groupId}/members`,
        { user_ids: [userId] },
        { headers: authHeaders() }
      );
      toast.success(t('admin.studentHub.sinav_added'));
      // Refresh both suggestions and detail
      fetchSuggestions();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setAddingGroupId(null);
    }
  };

  // Flatten suggestions for display: course-grouped + genel
  const allSuggestions = suggestions
    ? [
        ...(suggestions.suggestions || []),
        ...(suggestions.genel
          ? [{ course_id: '__genel__', course_title: lang === 'tr' ? 'Genel' : 'General', groups: suggestions.genel }]
          : []),
      ]
    : [];

  return (
    <div className="space-y-5">
      {/* ── Mevcut sınav grupları ─────────────────────────────────────────── */}
      <section>
        <h3 className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2">
          {t('admin.studentHub.sinav_my_groups')}
        </h3>
        {examGroups.length === 0 ? (
          <div className="text-xs text-[var(--text-muted)] py-3 px-2">
            {t('admin.studentHub.sinav_no_groups')}
          </div>
        ) : (
          <div className="space-y-2">
            {examGroups.map((grp, i) => (
              <div
                key={grp.group_id || i}
                className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)] bg-white"
                data-testid={`sinav-group-row-${i}`}
              >
                <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={16} className="text-cyan-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{grp.ad || grp.group_id}</p>
                  {grp.course_title && (
                    <p className="text-[10px] text-[var(--text-muted)] truncate">{grp.course_title}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {grp.sinav_tarihi && (
                    <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                      <Calendar size={11} />
                      {fmtDate(grp.sinav_tarihi, lang)}
                    </span>
                  )}
                  {typeof grp.uye_sayisi === 'number' && (
                    <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                      <Users size={11} />
                      {t('admin.studentHub.sinav_members').replace('{{n}}', grp.uye_sayisi)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Önerilen gruplar ─────────────────────────────────────────────── */}
      <section>
        <h3 className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2">
          {t('admin.studentHub.sinav_suggestions')}
        </h3>

        {suggestLoading ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-[3px] border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : allSuggestions.length === 0 ? (
          <EmptyState
            title={t('admin.studentHub.sinav_no_suggestions')}
            description={t('admin.studentHub.sinav_no_suggestions_desc')}
            icon={<GraduationCap size={36} strokeWidth={1.5} />}
          />
        ) : (
          <div className="space-y-4">
            {allSuggestions.map((courseBlock) => (
              <div key={courseBlock.course_id}>
                {courseBlock.course_id !== '__genel__' && (
                  <p className="text-xs font-bold text-[var(--text-main)] mb-2">
                    {courseBlock.course_title}
                  </p>
                )}
                <div className="space-y-2">
                  {(courseBlock.groups || []).map((grp, gi) => (
                    <div
                      key={grp.group_id || gi}
                      className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-[var(--border-light)] bg-gray-50/60"
                      data-testid={`sinav-suggest-row-${gi}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <GraduationCap size={16} className="text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{grp.ad || grp.group_id}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {grp.sinav_tarihi && (
                            <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                              <Calendar size={10} />
                              {fmtDate(grp.sinav_tarihi, lang)}
                            </span>
                          )}
                          {typeof grp.uye_sayisi === 'number' && (
                            <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                              <Users size={10} />
                              {t('admin.studentHub.sinav_members').replace('{{n}}', grp.uye_sayisi)}
                            </span>
                          )}
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleAddToGroup(grp.group_id)}
                        disabled={addingGroupId === grp.group_id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-[11px] font-bold hover:bg-emerald-600 disabled:opacity-40 flex-shrink-0"
                        data-testid={`sinav-add-btn-${gi}`}
                      >
                        {addingGroupId === grp.group_id ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus size={12} />
                        )}
                        {addingGroupId === grp.group_id
                          ? t('admin.studentHub.sinav_adding')
                          : t('admin.studentHub.sinav_btn_add_group')}
                      </motion.button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
